import { REAGION_SOATO } from '@/common/constants/regions';
import { useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ICottonAnalyticalResponse } from '../models/ICottonAnalytical';

interface CottonAnalyticalTableProps {
  data: ICottonAnalyticalResponse;
}

const STICKY_NUM_W = 46;
const STICKY_NAME_W = 200;
const STICKY_PLAN_W = 120;
const COL_W = 200;

export function CottonAnalyticalTable({ data }: CottonAnalyticalTableProps) {
  const { t } = useTranslation();
  const { open } = useSidebar();
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);

  const columns = (() => {
    const map = new Map<string, { id: string; name: string; order: number }>();
    for (const region of data.data) {
      for (const action of region.actions_data) {
        if (!map.has(action.action_id)) {
          map.set(action.action_id, {
            id: action.action_id,
            name: action.action_name,
            order: action.order,
          });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => a.order - b.order);
  })();

  useEffect(() => {
    const hEl = headerScrollRef.current;
    const bEl = bodyScrollRef.current;
    if (!hEl || !bEl) return;
    const onBody = () => {
      if (hEl.scrollLeft !== bEl.scrollLeft) hEl.scrollLeft = bEl.scrollLeft;
    };
    const onHead = () => {
      if (bEl.scrollLeft !== hEl.scrollLeft) bEl.scrollLeft = hEl.scrollLeft;
    };
    bEl.addEventListener('scroll', onBody);
    hEl.addEventListener('scroll', onHead);
    return () => {
      bEl.removeEventListener('scroll', onBody);
      hEl.removeEventListener('scroll', onHead);
    };
  }, []);

  const regions = useMemo(() => {
    return REAGION_SOATO
      .map((r) => data.data.find((d) => d.region_id === r.id))
      .filter(Boolean) as typeof data.data;
  }, [data.data]);

  const getCellValue = (regionIndex: number, actionId: string) => {
    const region = regions[regionIndex];
    const found = region.actions_data.find((a) => a.action_id === actionId);
    return found ? found.inspected_area : 0;
  };
  const getTotal = (actionId: string) => {
    const found = data.totals.find((t) => t.action_id === actionId);
    return found ? found.total : 0;
  };
  const fmt = (n: number) =>
    n.toLocaleString('ru-RU', { maximumFractionDigits: 1 });

  return (
    <div className="h-full w-full rounded-10 border bg-white overflow-hidden flex flex-col">
      {/* Fixed header */}
      <div
        ref={headerScrollRef}
        className={cn(
          'flex-shrink-0 border-b overflow-x-auto overflow-y-hidden scrollbar-hide',
          open ? 'w-[calc(100vw-320px)]' : 'w-[calc(100vw-150px)]',
        )}
      >
        <table
          className="caption-bottom text-sm border-collapse"
          style={{ width: 'max-content', minWidth: '100%' }}
        >
          <thead className="bg-gray-100">
            <tr className="border-b bg-gray-100 hover:bg-gray-100">
              <th
                className="h-10 text-[12px] font-medium text-black/60 border-b py-3 bg-gray-100 text-center align-middle px-2"
                style={{
                  width: STICKY_NUM_W,
                  minWidth: STICKY_NUM_W,
                  position: 'sticky',
                  left: 0,
                  zIndex: 12,
                }}
              >
                {t('no.')}
              </th>
              <th
                className="h-10 text-[12px] font-medium text-black/60 border-b py-3 bg-gray-100 text-left align-middle px-4"
                style={{
                  width: STICKY_NAME_W,
                  minWidth: STICKY_NAME_W,
                  position: 'sticky',
                  left: STICKY_NUM_W,
                  zIndex: 12,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
                }}
              >
                {t('region.name')}
              </th>
              <th
                className="h-10 text-[12px] font-medium text-black/60 border-b py-3 bg-gray-100 text-center align-middle px-1"
                style={{
                  width: STICKY_PLAN_W,
                  minWidth: STICKY_PLAN_W,
                  position: 'sticky',
                  left: STICKY_NUM_W + STICKY_NAME_W,
                  zIndex: 12,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
                }}
              >
                {t('monitored')}
              </th>
              {columns.map((col) => (
                <th
                  key={col.id}
                  className="h-10 text-[12px] font-medium text-black/60 border-b py-3 px-1 bg-gray-100 text-center align-middle"
                  style={{ width: COL_W, minWidth: COL_W }}
                >
                  <p className="text-sm font-medium text-dark-blue text-center">
                    {col.name}
                  </p>
                </th>
              ))}
            </tr>
          </thead>
        </table>
      </div>

      {/* Scrollable body */}
      <div
        ref={bodyScrollRef}
        className={cn(
          'flex-1 overflow-auto',
          open ? 'w-[calc(100vw-320px)]' : 'w-[calc(100vw-150px)]',
        )}
      >
        <table
          className="caption-bottom text-sm border-collapse"
          style={{ width: 'max-content', minWidth: '100%' }}
        >
          <thead className="invisible">
            <tr>
              <th className="h-0 px-2" style={{ width: STICKY_NUM_W }} />
              <th className="h-0 px-4" style={{ width: STICKY_NAME_W }} />
              <th className="h-0 px-1" style={{ width: STICKY_PLAN_W }} />
              {columns.map((a) => (
                <th key={a.id} className="h-0 px-1" style={{ width: COL_W }} />
              ))}
            </tr>
          </thead>

          <tbody>
            {regions.map((region, idx) => {
              const soato = REAGION_SOATO.find((r) => r.id === region.region_id)?.soato;
              const plan = data.plans?.find(
                (p) => p.region_soato === soato,
              );
              return (
                <tr
                  key={region.region_id}
                  className="border-b hover:bg-light duration-100"
                >
                  <td
                    className="h-[44px] text-center px-2 align-middle text-[12px] text-black/50 bg-white"
                    style={{
                      width: STICKY_NUM_W,
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                    }}
                  >
                    {idx + 1}
                  </td>
                  <td
                    className="h-[44px] px-4 align-middle font-medium text-[13px] bg-white"
                    style={{
                      width: STICKY_NAME_W,
                      position: 'sticky',
                      left: STICKY_NUM_W,
                      zIndex: 2,
                    }}
                  >
                    {region.region_name}
                  </td>
                  <td
                    className="h-[44px] px-1 text-center align-middle font-medium text-sm text-dark-blue bg-white"
                    style={{
                      width: STICKY_PLAN_W,
                      position: 'sticky',
                      left: STICKY_NUM_W + STICKY_NAME_W,
                      zIndex: 2,
                      boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
                    }}
                  >
                    {plan ? fmt(Number(plan.all_projected_area)) : '-'}
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col.id}
                      className="h-[44px] text-center px-1 align-middle text-sm font-medium text-dark-blue"
                      style={{ width: COL_W }}
                    >
                      {fmt(getCellValue(idx, col.id))}
                    </td>
                  ))}
                </tr>
              );
            })}

            {/* Totals row */}
            <tr className="border-b bg-gray-100 hover:bg-gray-100">
              <td
                className="h-[52px] text-center align-middle bg-gray-100 border-t"
                style={{
                  width: STICKY_NUM_W,
                  position: 'sticky',
                  left: 0,
                  zIndex: 2,
                }}
              />
              <td
                className="h-[52px] px-4 align-middle font-bold text-[13px] bg-gray-100 border-t"
                style={{
                  width: STICKY_NAME_W,
                  position: 'sticky',
                  left: STICKY_NUM_W,
                  zIndex: 2,
                }}
              >
                {t('total')}
              </td>
              <td
                className="h-[52px] px-1 text-center align-middle font-bold text-sm text-dark-blue bg-gray-100 border-t"
                style={{
                  width: STICKY_PLAN_W,
                  position: 'sticky',
                  left: STICKY_NUM_W + STICKY_NAME_W,
                  zIndex: 2,
                  boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
                }}
              >
                {fmt(
                  data.plans?.reduce(
                    (acc, p) => acc + Number(p.all_projected_area),
                    0,
                  ) || 0,
                )}
              </td>
              {columns.map((col) => (
                <td
                  key={col.id}
                  className="h-[52px] text-center px-1 align-middle text-sm font-medium text-dark-blue bg-gray-100 border-t"
                  style={{ width: COL_W }}
                >
                  {fmt(getTotal(col.id))}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
